/**
 * The client's picture of a fight.
 *
 * Pure transitions, for the same reason `messages.ts` is: what the room sees
 * mid-combat is assembled from eight separate events arriving in order, and
 * getting that wrong is silent — the panel simply shows the previous round.
 *
 * **There is no turn queue and no current actor.** Everyone declares in
 * parallel and initiative is rolled per side after the window closes, so the
 * strip shows *declaration status* — who has committed — which is the thing
 * the room actually needs while a window is open. Rendering a turn order would
 * be inventing a model the server does not have.
 */

export interface CombatantView {
  id: string;
  name: string;
  side: string;
  characterId?: string | undefined;
  hitPoints: number;
  maxHitPoints: number;
  conscious: boolean;
  /**
   * What was declared about this combatant's situation — knee-deep
   * water, higher ground, invisible. Absent is the ordinary case, so the panel
   * shows an untouched checklist rather than an empty one.
   */
  conditions?: readonly string[] | undefined;
  /**
   * The timed effects a table may read off this combatant — asleep, blurred, stunned, and (on the party's own
   * side) the protections its members know they hold. Short lowercase labels; absent when there are none.
   */
  effects?: readonly string[] | undefined;
  /**
   * Where this combatant stands on the encounter map, as a percentage of the
   * image — the same convention a `Waypoint` already uses. Absent is the
   * ordinary case: positioning is opt-in per encounter, and a fight with no
   * scaled map never sends one. Occupancy (`zones`) is still the thing every
   * other rule reads; a position is purely a second, optional picture drawn
   * over the same fight.
   */
  position?: { mapX: number; mapY: number } | undefined;
}

export interface CombatView {
  round: number;
  phase: 'declaring' | 'resolving' | 'ended';
  /**
   * A soft nudge. Nothing is cut off when it passes.
   *
   * Explicitly `| undefined` — like the DM status fields — so a transition can
   * clear it by assignment rather than by rebuilding the object around the
   * keys it wants to omit.
   */
  deadlineTs?: number | undefined;
  combatants: CombatantView[];
  /** Combatant ids that have declared this round, in arrival order. */
  declared: string[];
  /** What each declaration was, as announced to the room. */
  summaries: Record<string, string>;
  /** Absent until the window closes — it is rolled after, never before. */
  initiative?: Record<string, number> | undefined;
  /**
   * Who is standing where: `{ [defenderId]: { [attackerId]: zone } }`.
   *
   * Occupancy, never coordinates — which is what lets this render as prose
   * with no map at all.
   */
  zones?: Record<string, Record<string, string>> | undefined;
  /** Set once the fight is over, with what it came to. */
  outcome?: string | undefined;
  xpAwarded?: number | undefined;
  /**
   * Absent unless the Owner has opted this fight into real positions.
   * `combat:start` never carries one — a fresh fight has none yet — so this
   * only ever arrives on `zone:update` (set, or cleared) and the reconnect
   * snapshot.
   */
  mapScaleFeetPerPercent?: number | undefined;
}

export function combatStarted(
  round: number,
  combatants: CombatantView[],
  previous?: CombatView,
): CombatView {
  return {
    round,
    phase: 'declaring',
    combatants,
    // A mid-fight roster change (a combatant leaving the fight) re-emits
    // combat:start, and losing the declarations already made would ask the
    // room to declare twice. Only for the *same* round, though: a new fight
    // must not inherit the last one's declarations.
    declared: previous?.round === round ? previous.declared : [],
    summaries: previous?.round === round ? previous.summaries : {},
  };
}

export function roundBegan(view: CombatView, round: number): CombatView {
  // A new round clears the last one's declarations *and* its initiative:
  // leaving either up would show a number that no longer applies to anything.
  return { ...view, round, phase: 'declaring', declared: [], summaries: {}, initiative: undefined };
}

export function windowOpened(view: CombatView, round: number, deadlineTs?: number): CombatView {
  return { ...view, round, phase: 'declaring', deadlineTs };
}

export function declarationAcked(
  view: CombatView,
  combatantId: string,
  summary: string,
): CombatView {
  return {
    ...view,
    declared: view.declared.includes(combatantId)
      ? view.declared
      : [...view.declared, combatantId],
    summaries: { ...view.summaries, [combatantId]: summary },
  };
}

export function windowClosed(
  view: CombatView,
  round: number,
  initiative: Record<string, number>,
): CombatView {
  return { ...view, round, phase: 'resolving', initiative, deadlineTs: undefined };
}

/**
 * `zone:update` is a full, fresh projection every time it fires — never a
 * delta — so `mapScaleFeetPerPercent` is always REPLACED with whatever this
 * event carries, `undefined` included. Treating an absent field as "no
 * change" would make a cleared scale look permanent: the very next round's
 * ordinary re-seating would carry no `mapScaleFeetPerPercent` at all, and
 * preserving the old value would keep showing tokens for a fight that just
 * opted back out.
 */
export function combatantsUpdated(
  view: CombatView,
  combatants: CombatantView[],
  mapScaleFeetPerPercent?: number,
): CombatView {
  return { ...view, combatants, mapScaleFeetPerPercent };
}

export function combatEnded(view: CombatView, outcome: string, xpAwarded: number): CombatView {
  return { ...view, phase: 'ended', outcome, xpAwarded, deadlineTs: undefined };
}

/**
 * Who the room is still waiting on.
 *
 * Only conscious party combatants: a monster's choice is never public, and a
 * combatant that is down is not being waited for.
 */
export function waitingOn(view: CombatView): CombatantView[] {
  return view.combatants.filter(
    (c) => c.side === 'party' && c.conscious && !view.declared.includes(c.id),
  );
}

/**
 * Who is on a defender, as a sentence.
 *
 * Theatre of the Mind is a first-class combat surface in this design, not a
 * fallback: the engine tracks occupancy and nothing finer, so prose loses
 * none of the state a grid would show.
 */
export function zoneProse(view: CombatView, defenderId: string): string {
  const byAttacker = view.zones?.[defenderId];
  if (!byAttacker || Object.keys(byAttacker).length === 0) return '';
  const named = Object.entries(byAttacker).map(([attackerId, zone]) => {
    const name = view.combatants.find((c) => c.id === attackerId)?.name ?? attackerId;
    return `${name} (${zone})`;
  });
  return named.join(', ');
}

/** Ascending — **low acts first**, which is the whole of this ruleset's order. */
export function initiativeOrder(view: CombatView): { id: string; total: number }[] {
  if (!view.initiative) return [];
  return Object.entries(view.initiative)
    .map(([id, total]) => ({ id, total }))
    .sort((a, b) => a.total - b.total);
}
